import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import { X } from "lucide-react";
import { useResponsiveDialog } from "@/hooks/useResponsiveDialog";

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  htmlContent: string;
  subject?: string;
}

/**
 * Email Template Preview Modal
 *
 * Displays a preview of the compiled email template in an iframe.
 */
export function PreviewModal({
  open,
  onClose,
  htmlContent,
  subject,
}: PreviewModalProps) {
  const { fullScreen, TransitionComponent } = useResponsiveDialog();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      TransitionComponent={TransitionComponent}
      PaperProps={{
        sx: { height: fullScreen ? "100%" : "90vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Email Preview</Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close preview">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Subject Line Preview */}
          {subject && (
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Subject:
              </Typography>
              <Typography variant="body1">{subject}</Typography>
            </Box>
          )}

          {/* Email Body Preview */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              bgcolor: "grey.100",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Email Body Preview:
            </Typography>
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 1,
                border: 1,
                borderColor: "grey.300",
                overflow: "hidden",
                height: "calc(100% - 24px)",
              }}
            >
              <iframe
                srcDoc={htmlContent}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  minHeight: "500px",
                }}
                title="Email Preview"
              />
            </Box>
          </Box>

          {/* Help Text */}
          <Alert severity="info" sx={{ m: 2, mt: 0 }}>
            <Typography variant="body2">
              <strong>Preview Note:</strong> This preview shows how your email
              will look to contacts. Actual emails may render slightly
              differently depending on the recipient&apos;s email client.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
